import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ChevronLeft, ChevronRight, Download, ListOrdered, Route, ShieldCheck, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../common";
import { api, errMsg, downloadBlob } from "../../lib/api";
import { SceneSvg } from "./SceneSvg";

const KIND_LABEL = { main: "Main flow", journey: "Journey", overview: "Overview" };
const KIND_LABEL_HI = { main: "मुख्य प्रवाह", journey: "यात्रा", overview: "समग्र चित्र" };

/**
 * Phase 222 — "App Flow": picture-by-picture guide for the signed-in user's OWN role.
 * props:
 *  fetchPath   – GET returns {role, role_label, custom_role_name, municipality, guide}
 *  exportPath  – GET returns the PowerPoint
 *  compact     – phone layout (collector / marshal)
 */
export const AppFlowViewer = ({ fetchPath, exportPath, compact = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [active, setActive] = useState("all");
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const topRef = useRef(null);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const load = () => {
    setError(null);
    api.get(fetchPath).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  };
  useEffect(load, [fetchPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = data?.guide?.sections || [];
  const shown = useMemo(() => (active === "all" ? sections : sections.filter((s) => s.key === active)), [sections, active]);
  const totalSteps = data?.guide?.step_count || 0;

  const download = async () => {
    setDownloading(true);
    try {
      const fname = `MunicipalConnect-AppFlow-${(data?.role_label || "guide").replace(/[^A-Za-z0-9]+/g, "-")}.pptx`;
      await downloadBlob(exportPath, fname);
      toast.success("PowerPoint downloaded — it contains only your role's guide");
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(false); }
  };

  const jump = (key) => {
    setActive(key);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };

  if (error) {
    return (
      <EmptyState title="Could not load the App Flow" subtitle={!online ? "You are offline — the App Flow needs an internet connection." : error}
        action={<Button variant="outline" size="sm" onClick={load} data-testid="appflow-retry">{!online && <WifiOff className="h-4 w-4 mr-1.5" />}Retry</Button>} />
    );
  }
  if (!data) {
    return (
      <div className="space-y-3" data-testid="appflow-loading">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-10 w-2/3 rounded-lg" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  const g = data.guide;
  const idx = shown.length === 1 ? sections.findIndex((s) => s.key === shown[0].key) : -1;

  return (
    <div className="space-y-4" data-testid="appflow-viewer" ref={topRef}>
      {/* ---- header ---- */}
      <Card className="shadow-sm border-primary/20 bg-accent/30">
        <CardContent className={`${compact ? "p-4" : "p-5 sm:p-6"} flex flex-col lg:flex-row lg:items-start gap-4`}>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Route className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-xl font-semibold" data-testid="appflow-title">{g.title}</h2>
              <Badge variant="outline" className="text-[10px]" data-testid="appflow-role-badge">Your role: {data.role_label}</Badge>
              {data.custom_role_name && <Badge variant="secondary" className="text-[10px]" data-testid="appflow-custom-role">{data.custom_role_name} · follows the {data.role_label} guide</Badge>}
            </div>
            <p className="text-base text-muted-foreground" data-testid="appflow-title-hi">{g.title_hi}</p>
            <p className="text-sm leading-relaxed">{g.intro}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{g.intro_hi}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1" data-testid="appflow-scope-note">
              <ShieldCheck className="h-3.5 w-3.5" /> {sections.length} guides · {totalSteps} steps · Only the App Flow of your own role is shown here and in the download.
            </p>
          </div>
          <Button onClick={download} disabled={downloading || !online} className="shrink-0" data-testid="appflow-download-pptx">
            <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing…" : "Download as PowerPoint"}
          </Button>
        </CardContent>
      </Card>

      {/* ---- section chips ---- */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" data-testid="appflow-section-tabs">
        <button role="tab" aria-selected={active === "all"} onClick={() => jump("all")} data-testid="appflow-tab-all"
          className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent"}`}>
          All guides
        </button>
        {sections.map((s, i) => (
          <button key={s.key} role="tab" aria-selected={active === s.key} onClick={() => jump(s.key)} data-testid={`appflow-tab-${s.key}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active === s.key ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent"}`}>
            {i + 1}. {s.title}
          </button>
        ))}
      </div>

      {/* ---- sections ---- */}
      {shown.map((sec) => {
        const si = sections.findIndex((x) => x.key === sec.key) + 1;
        return (
          <section key={sec.key} className="space-y-4" data-testid={`appflow-section-${sec.key}`}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading font-semibold shrink-0">{si}</div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-lg font-semibold leading-tight">{sec.title}</h3>
                  <Badge variant={sec.kind === "main" ? "default" : "outline"} className="text-[10px]">{KIND_LABEL[sec.kind] || "Guide"} · {KIND_LABEL_HI[sec.kind] || ""}</Badge>
                  {sec.steps?.length > 0 && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><ListOrdered className="h-3.5 w-3.5" />{sec.steps.length} steps</span>}
                </div>
                <p className="text-sm text-muted-foreground">{sec.title_hi}</p>
              </div>
            </div>

            {sec.kind === "overview" && sec.scene && (
              <Card className="shadow-sm overflow-hidden" data-testid="appflow-overview-chart">
                <CardContent className="p-3 sm:p-4 space-y-3">
                  <SceneSvg scene={sec.scene} title={sec.title} />
                  <p className="text-sm leading-relaxed">{sec.summary}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{sec.summary_hi}</p>
                </CardContent>
              </Card>
            )}

            {(sec.steps || []).map((st, i) => (
              <React.Fragment key={st.n}>
                <Card className="shadow-sm overflow-hidden" data-testid={`appflow-step-${sec.key}-${st.n}`}>
                  <CardContent className={compact ? "p-3 space-y-3" : "p-4 sm:p-5 space-y-3"}>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-[#DC8F09] text-white flex items-center justify-center text-lg font-bold shrink-0" aria-label={`Step ${st.n}`}>{st.n}</div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-base leading-tight" data-testid={`appflow-step-title-${sec.key}-${st.n}`}>{st.title}</p>
                        <p className="text-sm text-muted-foreground">{st.title_hi}</p>
                      </div>
                      {st.approval && (
                        <div className="hidden sm:block shrink-0 max-w-[280px] rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-1.5 text-[11px] leading-snug" data-testid={`appflow-approval-${sec.key}-${st.n}`}>
                          <p className="font-semibold text-[hsl(var(--warning))]">Approval: {st.approval.by}{st.approval.what ? ` — ${st.approval.what}` : ""}</p>
                          <p className="text-[hsl(var(--warning))]/90">स्वीकृति: {st.approval.by_hi}{st.approval.what_hi ? ` — ${st.approval.what_hi}` : ""}</p>
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl border bg-[#F8FAFC] overflow-hidden">
                      <SceneSvg scene={st.scene} title={`${st.title} — screen drawing`} />
                    </div>
                    <div className="space-y-1">
                      <p className={`${compact ? "text-sm" : "text-base"} leading-relaxed`} data-testid={`appflow-step-caption-${sec.key}-${st.n}`}>{st.caption}</p>
                      <p className={`${compact ? "text-sm" : "text-base"} leading-relaxed text-muted-foreground`} data-testid={`appflow-step-caption-hi-${sec.key}-${st.n}`}>{st.caption_hi}</p>
                      {st.approval && (
                        <p className="sm:hidden text-[11px] text-[hsl(var(--warning))] font-medium pt-1">Approval: {st.approval.by}{st.approval.what ? ` — ${st.approval.what}` : ""} · स्वीकृति: {st.approval.by_hi}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                {i < sec.steps.length - 1 && (
                  <div className="flex justify-center -my-1" aria-hidden="true"><ArrowDown className="h-6 w-6 text-[#DC8F09]" /></div>
                )}
              </React.Fragment>
            ))}
          </section>
        );
      })}

      {/* ---- prev / next when one section is open ---- */}
      {idx >= 0 && (
        <div className="flex items-center justify-between pt-2" data-testid="appflow-pager">
          <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => jump(sections[idx - 1].key)} data-testid="appflow-prev">
            <ChevronLeft className="h-4 w-4 mr-1" /> {idx > 0 ? sections[idx - 1].title : "Start"}
          </Button>
          <span className="text-xs text-muted-foreground">{idx + 1} / {sections.length}</span>
          <Button variant="outline" size="sm" disabled={idx === sections.length - 1} onClick={() => jump(sections[idx + 1].key)} data-testid="appflow-next">
            {idx < sections.length - 1 ? sections[idx + 1].title : "End"} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
